import type React from 'react';

type ViewGroup =
  | 'classbook'
  | 'homework'
  | 'courses'
  | 'groups'
  | 'institution'
  | 'settings'
  | 'absences'
  | 'other'
  | 'meetings'
  | 'messages'
  | 'notifications'
  | 'menu';

export type View = {
  id: string;
  title: string;
  group: ViewGroup;
  component: React.ReactNode;
  closeable?: boolean;
  closeDepth?: number;
  onClose?: () => void;
};

export type StackConfig = {
  maxStackSize?: number;
  onStackChange?: (stack: View[]) => void;
  version?: number;
};

export class StackHandler {
  private stack: View[] = [];
  private config: StackConfig;
  public version: number = 1;

  constructor(config: StackConfig = {}) {
    this.version = config.version || 1;
    this.config = {
      maxStackSize: config.maxStackSize || 10,
      onStackChange: config.onStackChange,
    };
  }

  push(view: View) {
    if (this.stack.length > 0 && this.stack[0].group !== view.group) {
      const viewsToClose = [...this.stack];
      viewsToClose.forEach((v) => {
        v.onClose?.();
      });
      this.stack = [];
    }

    if (this.stack.length >= (this.config.maxStackSize || 10)) {
      this.stack.shift();
    }
    // Prevent duplicate views
    const existingIndex = this.stack.findIndex((v) => v.id === view.id);
    if (existingIndex !== -1) {
      this.stack = this.stack.slice(0, existingIndex);
    }
    this.stack.push(view);
    this.notifyChange();
  }

  pop(): View | undefined {
    const view = this.stack.pop();
    if (view?.onClose) {
      view.onClose();
    }
    this.notifyChange();
    return view;
  }

  closeToDepth(depth: number) {
    if (depth < 0 || depth >= this.stack.length) return;
    this.stack = this.stack.slice(0, depth + 1);
    this.notifyChange();
  }

  closeGroup(group: ViewGroup) {
    const index = this.stack.findIndex((v) => v.group === group);
    if (index !== -1) {
      this.closeToDepth(index - 1);
    }
  }

  getViewsByGroup(group: ViewGroup): View[] {
    return this.stack.filter((v) => v.group === group);
  }

  getCurrentView(): View | undefined {
    return this.stack[this.stack.length - 1];
  }

  getStack(): View[] {
    return [...this.stack];
  }

  getStackSize(): number {
    return this.stack.length;
  }

  clear() {
    const viewsToClose = [...this.stack];
    viewsToClose.forEach((view) => {
      view.onClose?.();
    });
    this.stack = [];
    this.notifyChange();
  }

  private notifyChange() {
    this.config.onStackChange?.(this.getStack());
  }

  closeById(id: string) {
    const index = this.stack.findIndex((v) => v.id === id);
    if (index !== -1) {
      if (index === this.stack.length - 1) {
        this.pop();
      } else {
        this.closeToDepth(index - 1);
      }
    }
  }
}
